import type { AuthenticationResponseJSON, PublicKeyCredentialRequestOptionsJSON } from '@simplewebauthn/browser';

/**
 * Talks to the two lanes through the Vite proxy: /p for signing in (public lane), /api for everything else.
 * The access token lives here in memory only; the refresh and device tokens are httpOnly cookies the browser
 * sends to /p/auth on its own. A 401 from /api triggers one refresh and one retry.
 */
export interface Session {
  sessionId: string;
  userId: string;
  accessToken: string;
  expiresAt: string;
  amr: string[];
  scope: 'full' | 'recovery';
  backupCodesLeft?: number;
}

let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setSessionLostHandler(fn: () => void) { onSessionLost = fn; }
export function hasAccessToken() { return accessToken !== null; }

async function postPublic<T>(path: string, body: unknown): Promise<{ status: number; body: T }> {
  const res = await fetch(`/p/auth/${path}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body), credentials: 'same-origin' });
  // only our lanes answer in JSON; anything else came from the edge (a WAF block, an error page)
  if (!res.headers.get('content-type')?.includes('application/json')) throw new ApiError(res.status, 'edge_refused');
  return { status: res.status, body: (await res.json()) as T };
}

export type BeginResult =
  | { status: 'signed_in'; session: Session }
  | { status: 'second_step'; challengeId: string; methods: ('totp' | 'backup_code')[]; expiresAt: string }
  | { status: 'setup_required'; grantId: string; expiresAt: string }
  | { status: 'failed' }
  | { status: 'locked'; until: string }
  | { status: 'expired' }
  | { error: string };

export async function beginWithPassword(email: string, password: string): Promise<BeginResult> {
  const r = await postPublic<BeginResult>('password', { email, password });
  if ('session' in r.body) accessToken = r.body.session.accessToken;
  return r.body;
}

export async function beginPasskeySignIn(): Promise<{ challengeId: string; options: PublicKeyCredentialRequestOptionsJSON }> {
  return (await postPublic<{ challengeId: string; options: PublicKeyCredentialRequestOptionsJSON }>('passkey/begin', {})).body;
}

export async function completePasskeySignIn(challengeId: string, response: AuthenticationResponseJSON): Promise<BeginResult> {
  const r = await postPublic<BeginResult>('passkey/complete', { challengeId, response });
  if ('session' in r.body) accessToken = r.body.session.accessToken;
  return r.body;
}

// ---- setup and recovery: a grant, kept in memory only, lets a person set their own details -------------------------

export type PasswordProblem = 'too_short' | 'too_long' | 'contains_email' | 'breached';
export type SetPasswordAnswer = { status: 'ok' } | { status: 'rejected'; problem: PasswordProblem } | { status: 'refused' } | { status: 'expired' };

export const setPassword = async (grantId: string, password: string) => (await postPublic<SetPasswordAnswer>('setup/password', { grantId, password })).body;
export const enrolAuthenticator = async (grantId: string) =>
  (await postPublic<{ status: 'ok'; secret: string; uri: string } | { status: 'expired' }>('setup/totp', { grantId })).body;
export const confirmAuthenticator = async (grantId: string, code: string) =>
  (await postPublic<{ status: 'ok'; backupCodes: string[] } | { status: 'failed' } | { status: 'expired' }>('setup/totp/confirm', { grantId, code })).body;
export const finishGrant = async (grantId: string) => { await postPublic('setup/finish', { grantId }); };

export const beginRecovery = async (email: string) => (await postPublic<{ status: 'sent'; challengeId: string }>('recovery', { email })).body;
export const completeRecovery = async (challengeId: string, emailCode: string, proof: { backupCode: string } | { approvalId: string }) =>
  (await postPublic<{ status: 'granted'; grantId: string } | { status: 'failed'; attemptsLeft?: number } | { status: 'expired' }>('recovery/complete', { challengeId, emailCode, ...proof })).body;

export async function completeSecondStep(challengeId: string, code: string, rememberDevice: boolean): Promise<BeginResult> {
  const r = await postPublic<BeginResult>('second-step', { challengeId, code, rememberDevice });
  if ('session' in r.body) accessToken = r.body.session.accessToken;
  return r.body;
}

let restoring: Promise<Session | null> | null = null;

/**
 * On load, or after a 401: try the refresh cookie. Refresh tokens rotate, so two calls in flight at once would
 * present the same token twice and the second would look like a reused token; every caller shares one request.
 * A failed request means no session for now, not a page that never finishes loading: the sign-in screen appears, and
 * the cookie is still there for the next load.
 */
export function restoreSession(): Promise<Session | null> {
  restoring ??= (async () => {
    try {
      const r = await postPublic<{ status: string; session?: Session }>('refresh', {});
      if (r.status === 200 && r.body.session) { accessToken = r.body.session.accessToken; return r.body.session; }
      accessToken = null;
      return null;
    } catch {
      accessToken = null;
      return null;
    } finally {
      restoring = null;
    }
  })();
  return restoring;
}

export async function signOut(): Promise<void> {
  await postPublic('sign-out', {});
  accessToken = null;
}

export class ApiError extends Error {
  /** `detail` is the server's own words for the person, where it has some. `edge_refused` means the answer wasn't ours: only our lanes answer in JSON. */
  constructor(public status: number, public code: string, public detail?: string) { super(code); }
}

export const api = <T,>(path: string, init: RequestInit = {}) => signedIn<T>(`/api${path}`, init);

/** A signed-in person's own sign-in details, on the public lane. */
export const account = <T,>(path: string, init: RequestInit = {}) => signedIn<T>(`/p/auth/account${path}`, init);

export interface AccountSummary {
  passkeys: { id: string; name: string; createdAt: string; lastUsedAt: string | null; backedUp: boolean }[];
  devices: { id: string; label: string; rememberedAt: string; lastSeenAt: string; expiresAt: string }[];
  backupCodesLeft: number;
}

async function signedIn<T>(url: string, init: RequestInit, retried = false): Promise<T> {
  const res = await fetch(url, { ...init, headers: { ...(init.headers ?? {}), ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}) } });
  if (res.status === 401 && !retried) {
    const s = await restoreSession();
    if (s) return signedIn<T>(url, init, true);
    onSessionLost?.();
  }
  if (!res.ok) {
    const ours = res.headers.get('content-type')?.includes('application/json') ?? false;
    const body = (ours ? await res.json().catch(() => ({})) : {}) as { error?: string; message?: string };
    throw new ApiError(res.status, body.error ?? (ours ? 'request_failed' : 'edge_refused'), body.message);
  }
  return (await res.json()) as T;
}
